// Shared helper to download PDF invoices/receipts reliably across web + native Capacitor.
// Flow:
//   1. Fetch backend token via XHR (bypasses Capacitor's native HTTP interception).
//   2. On web: open the signed download URL in a new tab.
//   3. On native Capacitor: download the PDF as base64, save via Filesystem plugin
//      and open natively using the FileOpener plugin.
//
// Plugin access is via window.Capacitor.Plugins.* so we don't bundle native deps
// into the web build. @capacitor/filesystem + @capacitor-community/file-opener are
// installed in /app/mobile (the Android wrapper) and auto-registered at runtime.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const isNativeCapacitor = () => {
  try {
    const cap = window.Capacitor;
    if (!cap) return false;
    if (typeof cap.isNativePlatform === "function") return cap.isNativePlatform();
    return !!cap.isNative;
  } catch {
    return false;
  }
};

const getDownloadToken = (type, id) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/invoices/download-token`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    xhr.withCredentials = true;
    xhr.onload = () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300 && data.token) resolve(data.token);
        else reject(new Error(data.detail || "Failed to get download token"));
      } catch {
        reject(new Error("Invalid token response"));
      }
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(JSON.stringify({ type, id }));
  });

const fetchPdfAsBase64 = (url) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("GET", url, true);
    xhr.responseType = "blob";
    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error("Download failed"));
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        // reader.result -> "data:application/pdf;base64,AAAA..."
        const base64 = String(reader.result).split(",")[1] || "";
        resolve(base64);
      };
      reader.onerror = () => reject(new Error("Base64 encode failed"));
      reader.readAsDataURL(xhr.response);
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send();
  });

const openPdfNatively = async (base64Data, filename) => {
  const plugins = (window.Capacitor && window.Capacitor.Plugins) || {};
  const Filesystem = plugins.Filesystem;
  const FileOpener = plugins.FileOpener;
  if (!Filesystem || !FileOpener) {
    throw new Error("Native plugins not available");
  }

  // Capacitor v7: Directory enum values are strings ("CACHE", "DOCUMENTS", ...)
  await Filesystem.writeFile({
    path: filename,
    data: base64Data,
    directory: "CACHE",
    recursive: true,
  });

  const uriRes = await Filesystem.getUri({
    path: filename,
    directory: "CACHE",
  });

  await FileOpener.open({
    filePath: uriRes.uri,
    contentType: "application/pdf",
  });
};

/**
 * Download & open a tax invoice / wallet receipt PDF.
 * @param {"order"|"wallet"|"recharge"} type
 * @param {string} id - Order ID, wallet txn ID, or recharge txn ID
 * @param {string} [filename] - Preferred filename when saving natively
 */
export const downloadInvoicePdf = async (type, id, filename = "Super-Morsel-Invoice.pdf") => {
  const token = await getDownloadToken(type, id);
  const url = `${API}/invoices/download/${token}`;

  if (isNativeCapacitor()) {
    try {
      const base64 = await fetchPdfAsBase64(url);
      await openPdfNatively(base64, filename);
      return;
    } catch (err) {
      // Fall through to web-style open as a last resort
      console.warn("Native PDF open failed, falling back to window.open", err);
    }
  }

  // Web browsers: open in a new tab (PDF rendered inline or downloaded by browser)
  window.open(url, "_blank");
};
